/**
 * Tasks — show view on the shared Inspector grammar (coverage §2.1).
 * Header / Overview / Relations / Timeline. The named primary action is
 * **set status** (a quick lifecycle change via `PATCH /api/tasks/:id`).
 *
 * The Audit feed for a task is the merged ARTIFACT timeline (the kernel chain
 * lands on the artifact, not the task), so this page mounts the `TaskTimeline`
 * panel keyed by `artifact_id` rather than the generic `/api/tasks/:id/timeline`
 * teaser — preserving the end-to-end trace the e2e asserts.
 */
import {
  useShow,
  useGo,
  useNavigation,
  useUpdate,
  type HttpError,
} from "@refinedev/core";
import { Boxes, UserRound } from "@/components/ui/icon";
import { TASK_STATUS, type Task } from "@aos/shared";

import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  ShowPageSkeleton,
  ShowPageError,
  FieldGrid,
  Field,
  RefLink,
  RelationsBlock,
  TechnicalId,
} from "@/components/inspector";
import { Markdown } from "@/components/app";
import { humanize } from "../projects/_form";
import { TaskTimeline } from "./TaskTimeline";

export function TaskShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { query } = useShow<Task, HttpError>({ resource: "tasks" });
  const { data, isLoading, isError, error, refetch } = query;
  const task = data?.data;
  const { mutate: updateTask, isLoading: updating } = useUpdate();

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !task) {
    return (
      <ShowPageError
        message={error?.message}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  // Tasks have no name; the instruction is the human-readable identity.
  // Use a trimmed first line as the inspector title, falling back to "Task".
  const instructionTitle = task.instruction?.trim().split("\n")[0] ?? "";
  const title =
    instructionTitle.length > 0
      ? instructionTitle.length > 80
        ? `${instructionTitle.slice(0, 80)}…`
        : instructionTitle
      : "Task";

  const setStatus = (next: string) =>
    updateTask({
      resource: "tasks",
      id: task.id,
      values: { task_status: next },
      successNotification: {
        type: "success",
        message: `Status set to ${humanize(next)}`,
      },
    });

  return (
    <Inspector>
      <InspectorHeader
        title={title}
        subtitle="An executable work item linked to an artifact — its instruction, status, and result."
        status={task.task_status}
        primaryAction={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={updating}>Set status</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {TASK_STATUS.map((s) => (
                <DropdownMenuItem
                  key={s}
                  disabled={s === task.task_status}
                  onClick={() => setStatus(s)}
                >
                  {humanize(s)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
        actions={
          <Button
            variant="outline"
            onClick={() => go({ to: editUrl("tasks", task.id) })}
          >
            <Icon name="edit" size={16} className="mr-2" /> Edit
          </Button>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Instruction" full>
            {task.instruction ? <Markdown source={task.instruction} /> : null}
          </Field>
          <Field label="Result" full>
            {task.result ? <Markdown source={task.result} /> : null}
          </Field>
          <Field label="Artifact">
            <RefLink resource="artifacts" id={task.artifact_id} />
          </Field>
          <Field label="Assignee">
            <RefLink resource="users" id={task.assignee_user_id} />
          </Field>
          <Field label="Created">
            {new Date(task.created_at).toLocaleString()}
          </Field>
          <Field label="Updated">
            {new Date(task.updated_at).toLocaleString()}
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "References",
              items: [
                {
                  key: "artifact",
                  label: "Artifact",
                  icon: Boxes,
                  node: <RefLink resource="artifacts" id={task.artifact_id} />,
                },
                {
                  key: "assignee",
                  label: "Assignee",
                  icon: UserRound,
                  node: (
                    <RefLink resource="users" id={task.assignee_user_id} />
                  ),
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={task.id} />
          </Field>
          <Field label="Artifact id">
            <TechnicalId id={task.artifact_id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Timeline">
        <TaskTimeline artifactId={task.artifact_id} flush />
      </InspectorSection>
    </Inspector>
  );
}

export default TaskShow;
